import { getWorkoutServicePromiseClient } from '../utils/grpcClient';
import * as pb from '../generated/fitness-service/v1/workout_pb';
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb';

// New workout interfaces for the updated system
export interface WorkoutPlan {
    id: string;
    title: string;
    description: string;
    trainer_id: string;
    client_id: string;
    start_date: string;
    end_date: string;
    is_active: boolean;
    goals: string;
    notes: string;
    difficulty: string;
    plan_type: string; // "strength", "cardio", "mixed", "weight_loss", "muscle_gain"

    daily_workouts: DailyWorkout[];
    created_at: string;
    updated_at: string;
    trainer: TrainerInfo;
    client: ClientInfo;
}

export interface DailyWorkout {
    id: string;
    workout_plan_id: string;
    day_number: number; // 1-14 for 2-week plan
    day_of_week: string; // "Monday", "Tuesday", etc.
    workout_date: string;
    title: string;
    description: string;
    estimated_duration: number;
    is_rest_day: boolean;
    notes: string;
    exercises: DailyWorkoutExercise[];
    created_at: string;
    updated_at: string;
}

export interface DailyWorkoutExercise {
    id: string;
    daily_workout_id: string;
    exercise_id: string;
    order_index: number;
    sets: number;
    reps: string;
    weight: string;
    rest_time: string;
    notes: string;
    is_superset: boolean;
    superset_group: number;
    is_warmup: boolean;
    is_cooldown: boolean;
    exercise: Exercise;
}

export interface Exercise {
    id: string;
    name: string;
    description: string;
    category: string;
    muscle_group: string;
    equipment: string;
    instructions: string;
    image_url: string;
    created_by: string;
    is_public: boolean;
    created_at: string;
    updated_at: string;
}

export interface TrainerInfo {
    id: string;
    name: string;
    email: string;
    phone: string;
    trainer_code: string;
    specialization: string;
    certification: string;
    experience_years: number;
    agency_name: string;
    is_active: boolean;
}

export interface ClientInfo {
    id: string;
    name: string;
    email: string;
}

export interface WorkoutStats {
    total_plans_created: number;
    active_plans: number;
    total_clients: number;
    client_stats: ClientStats[];
    plan_type_stats: PlanTypeStats[];
}

export interface ClientStats {
    client_name: string;
    total_plans: number;
    active_plans: number;
}

export interface PlanTypeStats {
    plan_type: string;
    count: number;
    percentage: number;
}

export interface GetWorkoutPlansParams {
    page?: number;
    limit?: number;
    client_id?: string;
    active_only?: boolean;
    search?: string;
}

export interface WorkoutPlansResponse {
    workout_plans: WorkoutPlan[];
    total_count: number;
    page: number;
    limit: number;
}

export interface CreateWorkoutPlanParams {
    title: string;
    description?: string;
    client_id: string;
    start_date: Date;
    goals?: string;
    notes?: string;
    difficulty?: string;
    plan_type?: string;
    daily_workouts: CreateDailyWorkoutParams[];
}

export interface CreateDailyWorkoutParams {
    day_number: number;
    day_of_week: string;
    workout_date: Date;
    title: string;
    description?: string;
    estimated_duration: number;
    is_rest_day: boolean;
    notes?: string;
    exercises: CreateDailyWorkoutExerciseParams[];
}

export interface CreateDailyWorkoutExerciseParams {
    exercise_id: string;
    order_index: number;
    sets: number;
    reps: string;
    weight?: string;
    rest_time?: string;
    notes?: string;
    is_superset?: boolean;
    superset_group?: number;
    is_warmup?: boolean;
    is_cooldown?: boolean;
}

export interface UpdateDailyWorkoutParams {
    daily_workout_id: string;
    title?: string;
    description?: string;
    estimated_duration?: number;
    is_rest_day?: boolean;
    notes?: string;
    exercises?: UpdateDailyWorkoutExerciseParams[];
}

export interface UpdateDailyWorkoutExerciseParams {
    id?: string; // For existing exercises
    exercise_id?: string; // For new exercises
    order_index?: number;
    sets?: number;
    reps?: string;
    weight?: string;
    rest_time?: string;
    notes?: string;
    is_superset?: boolean;
    superset_group?: number;
    is_warmup?: boolean;
    is_cooldown?: boolean;
    is_deleted?: boolean; // To mark exercises for deletion
}

class NewWorkoutService {
    private client = getWorkoutServicePromiseClient();
    private static instance: NewWorkoutService;

    static getInstance(): NewWorkoutService {
        if (!NewWorkoutService.instance) {
            NewWorkoutService.instance = new NewWorkoutService();
        }
        return NewWorkoutService.instance;
    }

    private getAuthMetadata() {
        const token = localStorage.getItem('authToken');
        return {
            Authorization: `Bearer ${token}`,
        };
    }

    // Create manual workout plan
    async createWorkoutPlan(params: CreateWorkoutPlanParams): Promise<WorkoutPlan> {
        const request = new pb.CreateWorkoutPlanRequest();
        request.setTitle(params.title);
        request.setClientId(params.client_id);

        const startTimestamp = new Timestamp();
        startTimestamp.fromDate(params.start_date);
        request.setStartDate(startTimestamp);

        if (params.description) request.setDescription(params.description);
        if (params.goals) request.setGoals(params.goals);
        if (params.notes) request.setNotes(params.notes);
        if (params.difficulty) request.setDifficulty(params.difficulty);
        if (params.plan_type) request.setPlanType(params.plan_type);

        // Add daily workouts
        const dailyWorkouts = params.daily_workouts.map(dw => {
            const dailyWorkout = new pb.CreateDailyWorkout();
            dailyWorkout.setDayNumber(dw.day_number);
            dailyWorkout.setDayOfWeek(dw.day_of_week);
            dailyWorkout.setTitle(dw.title);
            dailyWorkout.setEstimatedDuration(dw.estimated_duration);
            dailyWorkout.setIsRestDay(dw.is_rest_day);

            const workoutTimestamp = new Timestamp();
            workoutTimestamp.fromDate(dw.workout_date);
            dailyWorkout.setWorkoutDate(workoutTimestamp);

            if (dw.description) dailyWorkout.setDescription(dw.description);
            if (dw.notes) dailyWorkout.setNotes(dw.notes);

            // Add exercises
            const exercises = dw.exercises.map(ex => {
                const exercise = new pb.CreateDailyWorkoutExercise();
                exercise.setExerciseId(ex.exercise_id);
                exercise.setOrderIndex(ex.order_index);
                exercise.setSets(ex.sets);
                exercise.setReps(ex.reps);

                if (ex.weight) exercise.setWeight(ex.weight);
                if (ex.rest_time) exercise.setRestTime(ex.rest_time);
                if (ex.notes) exercise.setNotes(ex.notes);
                if (ex.is_superset) exercise.setIsSuperset(ex.is_superset);
                if (ex.superset_group) exercise.setSupersetGroup(ex.superset_group);
                if (ex.is_warmup) exercise.setIsWarmup(ex.is_warmup);
                if (ex.is_cooldown) exercise.setIsCooldown(ex.is_cooldown);

                return exercise;
            });

            dailyWorkout.setExercisesList(exercises);
            return dailyWorkout;
        });

        request.setDailyWorkoutsList(dailyWorkouts);

        try {
            const response = await this.client.createWorkoutPlan(request, this.getAuthMetadata());
            return this.mapWorkoutPlan(response.getWorkoutPlan()!);
        } catch (error) {
            console.error('Error creating workout plan:', error);
            throw this.handleError(error);
        }
    }

    // Get workout plans with new features
    async getWorkoutPlans(params: GetWorkoutPlansParams = {}): Promise<WorkoutPlansResponse> {
        const request = new pb.GetWorkoutPlansRequest();

        if (params.page !== undefined) request.setPage(params.page);
        if (params.limit !== undefined) request.setLimit(params.limit);
        if (params.client_id) request.setClientId(params.client_id);
        if (params.active_only !== undefined) request.setActiveOnly(params.active_only);
        if (params.search) request.setSearch(params.search);

        try {
            const response = await this.client.getWorkoutPlans(request, this.getAuthMetadata());

            const workoutPlans: WorkoutPlan[] = response.getWorkoutPlansList().map((plan: any) =>
                this.mapWorkoutPlan(plan)
            );

            return {
                workout_plans: workoutPlans,
                total_count: response.getTotalCount(),
                page: response.getPage(),
                limit: response.getLimit(),
            };
        } catch (error) {
            console.error('Error fetching workout plans:', error);
            throw this.handleError(error);
        }
    }

    // Get single workout plan with daily workouts
    async getWorkoutPlan(workoutPlanId: string): Promise<WorkoutPlan> {
        const request = new pb.GetWorkoutPlanRequest();
        request.setWorkoutPlanId(workoutPlanId);

        try {
            const response = await this.client.getWorkoutPlan(request, this.getAuthMetadata());
            return this.mapWorkoutPlan(response.getWorkoutPlan()!);
        } catch (error) {
            console.error('Error fetching workout plan:', error);
            throw this.handleError(error);
        }
    }

    // Get daily workouts for a plan
    async getDailyWorkouts(workoutPlanId: string, page: number = 1, limit: number = 14): Promise<{
        daily_workouts: DailyWorkout[];
        total_count: number;
    }> {
        const request = new pb.GetDailyWorkoutsRequest();
        request.setWorkoutPlanId(workoutPlanId);
        request.setPage(page);
        request.setLimit(limit);

        try {
            const response = await this.client.getDailyWorkouts(request, this.getAuthMetadata());

            const dailyWorkouts: DailyWorkout[] = response.getDailyWorkoutsList().map((dailyWorkout: any) =>
                this.mapDailyWorkout(dailyWorkout)
            );

            return {
                daily_workouts: dailyWorkouts,
                total_count: response.getTotalCount(),
            };
        } catch (error) {
            console.error('Error fetching daily workouts:', error);
            throw this.handleError(error);
        }
    }

    // Get single daily workout
    async getDailyWorkout(dailyWorkoutId: string): Promise<DailyWorkout> {
        const request = new pb.GetDailyWorkoutRequest();
        request.setDailyWorkoutId(dailyWorkoutId);

        try {
            const response = await this.client.getDailyWorkout(request, this.getAuthMetadata());
            return this.mapDailyWorkout(response.getDailyWorkout()!);
        } catch (error) {
            console.error('Error fetching daily workout:', error);
            throw this.handleError(error);
        }
    }

    // Get workout statistics
    async getWorkoutStats(startDate?: Date, endDate?: Date): Promise<WorkoutStats> {
        const request = new pb.GetWorkoutStatsRequest();

        if (startDate) {
            const startTimestamp = new Timestamp();
            startTimestamp.fromDate(startDate);
            request.setStartDate(startTimestamp);
        }

        if (endDate) {
            const endTimestamp = new Timestamp();
            endTimestamp.fromDate(endDate);
            request.setEndDate(endTimestamp);
        }

        try {
            const response = await this.client.getWorkoutStats(request, this.getAuthMetadata());

            return {
                total_plans_created: response.getTotalPlansCreated(),
                active_plans: response.getActivePlans(),
                total_clients: response.getTotalClients(),
                client_stats: response.getClientStatsList().map((stat: any) => ({
                    client_name: stat.getClientName(),
                    total_plans: stat.getTotalPlans(),
                    active_plans: stat.getActivePlans(),
                })),
                plan_type_stats: response.getPlanTypeStatsList().map((stat: any) => ({
                    plan_type: stat.getPlanType(),
                    count: stat.getCount(),
                    percentage: stat.getPercentage(),
                })),
            };
        } catch (error) {
            console.error('Error fetching workout stats:', error);
            throw this.handleError(error);
        }
    }

    // Helper method to map WorkoutPlan from protobuf
    private mapWorkoutPlan(plan: any): WorkoutPlan {
        return {
            id: plan.getId(),
            title: plan.getTitle(),
            description: plan.getDescription(),
            trainer_id: plan.getTrainerId(),
            client_id: plan.getClientId(),
            start_date: plan.getStartDate()?.toDate().toISOString() || '',
            end_date: plan.getEndDate()?.toDate().toISOString() || '',
            is_active: plan.getIsActive(),
            goals: plan.getGoals(),
            notes: plan.getNotes(),
            difficulty: plan.getDifficulty(),
            plan_type: plan.getPlanType(),

            daily_workouts: plan.getDailyWorkoutsList().map((dailyWorkout: any) =>
                this.mapDailyWorkout(dailyWorkout)
            ),
            created_at: plan.getCreatedAt()?.toDate().toISOString() || '',
            updated_at: plan.getUpdatedAt()?.toDate().toISOString() || '',
            trainer: this.mapTrainerInfo(plan.getTrainer()),
            client: this.mapClientInfo(plan.getClient()),
        };
    }

    // Helper method to map DailyWorkout from protobuf
    private mapDailyWorkout(dailyWorkout: any): DailyWorkout {
        return {
            id: dailyWorkout.getId(),
            workout_plan_id: dailyWorkout.getWorkoutPlanId(),
            day_number: dailyWorkout.getDayNumber(),
            day_of_week: dailyWorkout.getDayOfWeek(),
            workout_date: dailyWorkout.getWorkoutDate()?.toDate().toISOString() || '',
            title: dailyWorkout.getTitle(),
            description: dailyWorkout.getDescription(),
            estimated_duration: dailyWorkout.getEstimatedDuration(),
            is_rest_day: dailyWorkout.getIsRestDay(),
            notes: dailyWorkout.getNotes(),
            exercises: dailyWorkout.getExercisesList().map((exercise: any) => ({
                id: exercise.getId(),
                daily_workout_id: exercise.getDailyWorkoutId(),
                exercise_id: exercise.getExerciseId(),
                order_index: exercise.getOrderIndex(),
                sets: exercise.getSets(),
                reps: exercise.getReps(),
                weight: exercise.getWeight(),
                rest_time: exercise.getRestTime(),
                notes: exercise.getNotes(),
                is_superset: exercise.getIsSuperset(),
                superset_group: exercise.getSupersetGroup(),
                is_warmup: exercise.getIsWarmup(),
                is_cooldown: exercise.getIsCooldown(),
                exercise: exercise.getExercise() ? this.mapExercise(exercise.getExercise()) : {} as Exercise,
            })),
            created_at: dailyWorkout.getCreatedAt()?.toDate().toISOString() || '',
            updated_at: dailyWorkout.getUpdatedAt()?.toDate().toISOString() || '',
        };
    }

    // Helper method to map Exercise from protobuf
    private mapExercise(exercise: any): Exercise {
        if (!exercise) {
            return {} as Exercise;
        }

        return {
            id: exercise.getId() || '',
            name: exercise.getName() || '',
            description: exercise.getDescription() || '',
            category: exercise.getCategory() || '',
            muscle_group: exercise.getMuscleGroup() || '',
            equipment: exercise.getEquipment() || '',
            instructions: exercise.getInstructions() || '',
            image_url: exercise.getImageUrl() || '',
            created_by: exercise.getCreatedBy() || '',
            is_public: exercise.getIsPublic() || false,
            created_at: exercise.getCreatedAt()?.toDate()?.toISOString() || '',
            updated_at: exercise.getUpdatedAt()?.toDate()?.toISOString() || '',
        };
    }

    // Helper method to map TrainerInfo from protobuf
    private mapTrainerInfo(trainer: any): TrainerInfo {
        if (!trainer) {
            return {} as TrainerInfo;
        }

        return {
            id: trainer.getId() || '',
            name: trainer.getName() || '',
            email: trainer.getEmail() || '',
            phone: trainer.getPhone() || '',
            trainer_code: trainer.getTrainerCode() || '',
            specialization: trainer.getSpecialization() || '',
            certification: trainer.getCertification() || '',
            experience_years: trainer.getExperienceYears() || 0,
            agency_name: trainer.getAgencyName() || '',
            is_active: trainer.getIsActive() || false,
        };
    }

    // Helper method to map ClientInfo from protobuf
    private mapClientInfo(client: any): ClientInfo {
        if (!client) {
            return {} as ClientInfo;
        }

        return {
            id: client.getId() || '',
            name: client.getName() || '',
            email: client.getEmail() || '',
        };
    }

    // Update daily workout with exercises
    async updateDailyWorkout(params: UpdateDailyWorkoutParams): Promise<DailyWorkout> {
        const request = new pb.UpdateDailyWorkoutRequest();
        request.setDailyWorkoutId(params.daily_workout_id);

        if (params.title) request.setTitle(params.title);
        if (params.description) request.setDescription(params.description);
        if (params.estimated_duration !== undefined) request.setEstimatedDuration(params.estimated_duration);
        if (params.is_rest_day !== undefined) request.setIsRestDay(params.is_rest_day);
        if (params.notes) request.setNotes(params.notes);

        // Add exercises
        if (params.exercises) {
            const exercises = params.exercises.map(ex => {
                const exercise = new pb.UpdateDailyWorkoutExercise();
                if (ex.id) exercise.setId(ex.id);
                if (ex.exercise_id) exercise.setExerciseId(ex.exercise_id);
                exercise.setOrderIndex(ex.order_index || 0);
                exercise.setSets(ex.sets || 0);
                exercise.setReps(ex.reps || '');
                if (ex.weight) exercise.setWeight(ex.weight);
                if (ex.rest_time) exercise.setRestTime(ex.rest_time);
                if (ex.notes) exercise.setNotes(ex.notes);
                if (ex.is_superset !== undefined) exercise.setIsSuperset(ex.is_superset);
                if (ex.superset_group !== undefined) exercise.setSupersetGroup(ex.superset_group);
                if (ex.is_warmup !== undefined) exercise.setIsWarmup(ex.is_warmup);
                if (ex.is_cooldown !== undefined) exercise.setIsCooldown(ex.is_cooldown);
                if (ex.is_deleted !== undefined) exercise.setIsDeleted(ex.is_deleted);
                return exercise;
            });
            request.setExercisesList(exercises);
        }

        try {
            const response = await this.client.updateDailyWorkout(request, this.getAuthMetadata());
            return this.mapDailyWorkout(response.getDailyWorkout()!);
        } catch (error) {
            console.error('Error updating daily workout:', error);
            throw this.handleError(error);
        }
    }

    // Helper method to handle errors
    private handleError(error: any): Error {
        if (error && typeof error === 'object' && 'code' in error) {
            const grpcError = error as any;
            if (grpcError.code === 16) {
                return new Error('Authentication required. Please log in again.');
            } else if (grpcError.code === 7) {
                return new Error('Access denied. You do not have permission to perform this action.');
            } else if (grpcError.code === 5) {
                return new Error('Resource not found.');
            } else if (grpcError.code === 3) {
                return new Error('Invalid request parameters.');
            }
        }
        return error;
    }
}

export const newWorkoutService = NewWorkoutService.getInstance();
